import { Users, MessageSquareText, Wrench, Calendar } from "lucide-react";
import { prisma } from "@/lib/db";
import { requireAdmin } from "@/lib/admin-guard";

export const dynamic = "force-dynamic";

async function getStats() {
  const now = new Date();
  const dayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

  const [totalUsers, signups24h, signups7d, totalCharts, aiMessages24h, toolUses24h, recentLogs] =
    await Promise.all([
      prisma.user.count(),
      prisma.user.count({ where: { createdAt: { gte: dayAgo } } }),
      prisma.user.count({ where: { createdAt: { gte: weekAgo } } }),
      prisma.chart.count(),
      prisma.aIMessage.count({ where: { createdAt: { gte: dayAgo }, role: "user" } }),
      prisma.toolHistory.count({ where: { createdAt: { gte: dayAgo } } }),
      prisma.auditLog.findMany({
        orderBy: { createdAt: "desc" },
        take: 8,
      }),
    ]);

  return {
    totalUsers,
    signups24h,
    signups7d,
    totalCharts,
    aiMessages24h,
    toolUses24h,
    recentLogs,
  };
}

function StatCard({
  label,
  value,
  hint,
  Icon,
}: {
  label: string;
  value: string | number;
  hint?: string;
  Icon: typeof Users;
}) {
  return (
    <div className="border-stroke-subtle bg-bg-elevated/40 rounded-xl border p-5">
      <div className="flex items-center justify-between">
        <span className="text-ink-muted text-[11px] tracking-[0.18em] uppercase">{label}</span>
        <Icon className="text-brand-gold/60 h-4 w-4" />
      </div>
      <div className="font-display text-ink-primary mt-2 text-3xl font-semibold">{value}</div>
      {hint ? <div className="text-ink-muted mt-1 text-xs">{hint}</div> : null}
    </div>
  );
}

export default async function AdminDashboard() {
  await requireAdmin();
  const s = await getStats();

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-display text-ink-primary text-2xl font-semibold lg:text-3xl">
          Tổng quan
        </h1>
        <p className="text-ink-secondary mt-1 text-sm">
          Số liệu sống cập nhật mỗi request — không cache.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard
          label="Tổng user"
          value={s.totalUsers}
          hint={`+${s.signups7d} trong 7 ngày`}
          Icon={Users}
        />
        <StatCard label="Đăng ký 24h" value={s.signups24h} hint="user mới hôm nay" Icon={Users} />
        <StatCard
          label="AI message 24h"
          value={s.aiMessages24h}
          hint="số câu hỏi luận giải"
          Icon={MessageSquareText}
        />
        <StatCard
          label="Tool use 24h"
          value={s.toolUses24h}
          hint={`${s.totalCharts} lá số tổng`}
          Icon={Wrench}
        />
      </div>

      <div className="mt-8">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-display text-ink-primary text-lg font-semibold">Hoạt động gần đây</h2>
          <a href="/admin/audit" className="text-brand-gold hover:text-brand-gold-soft text-xs">
            Xem tất cả →
          </a>
        </div>

        {s.recentLogs.length === 0 ? (
          <div className="border-stroke-subtle bg-bg-elevated/40 text-ink-muted rounded-xl border p-6 text-center text-sm">
            Chưa có hoạt động admin nào được ghi nhận.
          </div>
        ) : (
          <div className="border-stroke-subtle bg-bg-elevated/40 divide-stroke-subtle/50 divide-y rounded-xl border">
            {s.recentLogs.map((log) => (
              <div key={log.id} className="flex items-start gap-3 p-4">
                <Calendar className="text-brand-gold/60 mt-0.5 h-4 w-4 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-ink-primary text-sm">
                    <span className="font-medium">{log.actorName}</span>{" "}
                    <span className="text-ink-secondary">{log.action}</span>
                    {log.target ? <span className="text-ink-muted"> → {log.target}</span> : null}
                  </div>
                  <div className="text-ink-muted mt-0.5 text-xs">
                    {log.createdAt.toLocaleString("vi-VN")}
                    {log.ipAddress ? ` · ${log.ipAddress}` : ""}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
